import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { ChevronDown, ChevronUp } from 'lucide-react-native';
import { type ReactNode, useEffect, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { ProfileSectionTitle } from '@/components/profile/ProfileText';
import { Badge, BadgeText } from '@/components/ui/badge';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import {
  trackProfileClinicalTrialsShowLess,
  trackProfileClinicalTrialsShowMore,
  trackProfilePublicationsShowLess,
  trackProfilePublicationsShowMore,
} from '@/util/analyticsEvents';

interface ProfileSectionProps<TItem> {
  title: string;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Items loaded so far (across all fetched pages). */
  items: TItem[];
  /** Server-reported total, shown as the count badge in the header. */
  total: number;
  emptyText: string;
  renderItem: (item: TItem, index: number) => ReactNode;
  keyExtractor: (item: TItem, index: number) => string;
  /** Number of items shown before the user expands. Defaults to 5. */
  initialVisible?: number;
  /** True when more pages can be fetched (infinite query). */
  hasNextPage?: boolean;
  /** True while the next page is being fetched. */
  isFetchingNextPage?: boolean;
  /** Fetches and appends the next page. */
  onLoadMore?: () => void;
  /** Expert whose profile this section belongs to — used for analytics. */
  expertId?: number;
  /** Identifies this section in analytics events. */
  sectionKey?: 'publications' | 'trials';
  /**
   * Optional. Called when the user collapses an expanded list via "Show less".
   * Lets the parent scroll the section header back into view using a
   * cross-platform ScrollView ref. When omitted, the section falls back to
   * the web-only DOM scrollIntoView path so callers that don't own a
   * ScrollView ref (e.g. the desktop sidebar) keep working.
   */
  onCollapseScroll?: () => void;
  /** Rendered between the title and the list, such as tabs. */
  headerBelow?: ReactNode;
  /** Shows the count badge beside the title. Off when tabs carry the counts. */
  showTitleCount?: boolean;
  /**
   * Collapses the list back to its first page whenever this value changes.
   * Lets a parent that swaps the list's content, such as a tab switch, reset
   * the expanded state without remounting the section, which would also
   * remount `headerBelow` and drop keyboard and screen reader focus.
   */
  collapseKey?: string;
}

const CONTAINER = 'mx-auto w-full max-w-5xl px-6';

/**
 * Generic section shell for expert profile lists. Owns the header, count badge,
 * and loading / empty / error states. Each section renders its own query so a
 * slow or failed section never blocks the rest of the profile. Supports
 * progressive disclosure — the list expands one page at a time on demand.
 */
export function ProfileSection<TItem>({
  title,
  isLoading,
  isError,
  onRetry,
  items,
  total,
  emptyText,
  renderItem,
  keyExtractor,
  initialVisible = 5,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  expertId,
  sectionKey,
  onCollapseScroll,
  headerBelow,
  showTitleCount = true,
  collapseKey,
}: ProfileSectionProps<TItem>) {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsExpanded(false);
  }, [collapseKey]);

  const visibleItems = isExpanded ? items : items.slice(0, initialVisible);

  // Collapsed with more to reveal → expand. Expanded with more pages → load them.
  const canExpand = !isExpanded && total > initialVisible;

  const canLoadMore = isExpanded && hasNextPage;

  const showMore = canExpand || canLoadMore;

  // DOM id so "Show less" can scroll the section heading back into view on
  // collapse, instead of leaving the user stranded deep in the page (web).
  const sectionId = `profile-section-${title.toLowerCase().replace(/\s+/g, '-')}`;

  const handleExpand = () => {
    setIsExpanded(true);

    if (expertId !== undefined) {
      if (sectionKey === 'publications') {
        trackProfilePublicationsShowMore({ expertId, isPagination: false, totalCount: total });
      } else if (sectionKey === 'trials') {
        trackProfileClinicalTrialsShowMore({ expertId, isPagination: false, totalCount: total });
      }
    }
  };

  const handleLoadMore = () => {
    onLoadMore?.();

    if (expertId !== undefined) {
      if (sectionKey === 'publications') {
        trackProfilePublicationsShowMore({ expertId, isPagination: true, totalCount: total });
      } else if (sectionKey === 'trials') {
        trackProfileClinicalTrialsShowMore({ expertId, isPagination: true, totalCount: total });
      }
    }
  };

  const handleCollapse = () => {
    setIsExpanded(false);

    if (expertId !== undefined) {
      if (sectionKey === 'publications') {
        trackProfilePublicationsShowLess({ expertId });
      } else if (sectionKey === 'trials') {
        trackProfileClinicalTrialsShowLess({ expertId });
      }
    }

    if (onCollapseScroll) {
      onCollapseScroll();

      return;
    }

    if (isWeb) {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  };

  function renderBody() {
    if (isLoading) {
      return (
        <VStack className={twJoin(CONTAINER, 'gap-4')}>
          {[0, 1, 2].map((row) => (
            <VStack key={row} className="gap-2">
              <Skeleton className="h-4 w-11/12 rounded" />
              <Skeleton className="h-3 w-2/5 rounded" />
            </VStack>
          ))}
        </VStack>
      );
    }

    if (isError) {
      return (
        <VStack className={twJoin(CONTAINER, 'gap-1')}>
          <Text className="text-sm text-typography-500">Couldn’t load {title.toLowerCase()}.</Text>
          <Pressable
            onPress={onRetry}
            hitSlop={getAccessibleHitSlop(20)}
            className="self-start web:cursor-pointer"
            {...getAccessibilityProps({ role: 'button', label: `Retry loading ${title}` })}>
            <Text className="font-medium text-sm text-primary-600 web:hover:text-primary-700 web:hover:underline">
              Retry
            </Text>
          </Pressable>
        </VStack>
      );
    }

    if (items.length === 0) {
      return (
        <View className={CONTAINER}>
          <Text className="text-sm text-typography-500">{emptyText}</Text>
        </View>
      );
    }

    return (
      <>
        {visibleItems.map((item, index) => (
          <View
            key={keyExtractor(item, index)}
            className={twJoin(CONTAINER, index !== visibleItems.length - 1 && 'mb-4')}>
            {renderItem(item, index)}
          </View>
        ))}

        {showMore || isExpanded ? (
          <HStack className={twJoin(CONTAINER, 'mt-3 items-center gap-4')}>
            {showMore ? (
              <Pressable
                onPress={canExpand ? handleExpand : handleLoadMore}
                disabled={isFetchingNextPage}
                hitSlop={getAccessibleHitSlop(20)}
                className="web:cursor-pointer"
                accessibilityState={{ disabled: isFetchingNextPage }}
                {...getAccessibilityProps({
                  role: 'button',
                  label: `Show more ${title.toLowerCase()}`,
                })}>
                {isFetchingNextPage ? (
                  <Spinner size="small" />
                ) : (
                  <HStack className="items-center gap-1">
                    <Text className="font-medium text-sm text-primary-600 web:hover:text-primary-700 web:hover:underline">
                      Show more
                    </Text>
                    <Icon as={ChevronDown} size="xs" className="text-primary-600" aria-hidden />
                  </HStack>
                )}
              </Pressable>
            ) : null}

            {isExpanded ? (
              <Pressable
                onPress={handleCollapse}
                hitSlop={getAccessibleHitSlop(20)}
                className="web:cursor-pointer"
                {...getAccessibilityProps({
                  role: 'button',
                  label: `Show fewer ${title.toLowerCase()}`,
                })}>
                <HStack className="items-center gap-1">
                  <Text className="font-medium text-sm text-primary-600 web:hover:text-primary-700 web:hover:underline">
                    Show less
                  </Text>
                  <Icon as={ChevronUp} size="xs" className="text-primary-600" aria-hidden />
                </HStack>
              </Pressable>
            ) : null}
          </HStack>
        ) : null}
      </>
    );
  }

  const showCount = showTitleCount && !isLoading && !isError && total > 0;

  return (
    <View nativeID={sectionId} className="web:[scroll-margin-top:16px]">
      <HStack className={twJoin(CONTAINER, 'mb-3 items-center justify-between')}>
        <HStack className="items-center gap-2">
          <ProfileSectionTitle className="text-left">{title}</ProfileSectionTitle>

          {showCount ? (
            <Badge action="muted" size="sm">
              <BadgeText>{total}</BadgeText>
            </Badge>
          ) : null}
        </HStack>
      </HStack>

      {headerBelow ? <View className={twJoin(CONTAINER, 'mb-3')}>{headerBelow}</View> : null}

      {renderBody()}
    </View>
  );
}
